import type { Exam, ExamState } from '../exam.js';
import { anySetupStepOngoing } from '../api/pending-tasks/extraction-or-generation-finished.js';
import { anyLiveJobForTypes } from '../api/jobs/job-store.js';
import { stepTypesOf } from '../api/jobs/job.js';
import { createTeacherSummary } from '../api/generate-grading-reports.js';

/** Why an exam's results cannot leave the teacher's hands yet. Null means they can. */
export type ResultsBlockedReason = 'grading-incomplete' | 'open-clarifications' | 'no-submissions';

/**
 * Whether any submission still has an AI grading the teacher has neither approved nor out-confidenced.
 *
 * LIFTED VERBATIM from the two places that already gate PDF/CSV export on it
 * (`generateGradingReportsMutation` and the `download_summary_reports` tool), which carried byte-identical
 * copies of this predicate. Distribution is the third caller and the reason to have one definition: a
 * result that is not fit to print is not fit to hand to the student it is about.
 *
 * Note it is a CONTENT+LANGUAGE-USE check only, and `isApprovedByTask[k] === true` means "nothing in task
 * k's subtree is unresolved" rather than "a human approved it" — a never-graded node reads as approved,
 * which is why `isGradingComplete` below is a separate, required condition rather than an implied one.
 */
export function hasOpenClarifications(exam: Exam | Record<string, unknown>, submissions: unknown[]): boolean {
  const summary = createTeacherSummary(exam, submissions as any[]);
  return summary.studentSummaries.some(
    student =>
      summary.activeTaskDefinitions.some(taskDef => student.isApprovedByTask[taskDef.taskKey] !== true) ||
      student.hasOpenLanguageUseClarification,
  );
}

/**
 * Whether grading has actually finished for this exam — not merely started, and not still settling.
 *
 * The conditions mirror what `closeGradingSession` (agents/gradingAgent.ts) itself waits for before it
 * writes `COMPLETED`, because that write is the only thing that sets the status and it defers while either
 * the in-process refcount or the job queue still holds work. Checking the status alone would pass an exam
 * whose next grading phase is queued but not yet claimed.
 */
export async function isGradingComplete(examId: string, examState: ExamState | undefined): Promise<boolean> {
  if (!examState?.hasGradingStarted) return false;
  if (examState.gradingProgress?.status !== 'COMPLETED') return false;
  if (Number(examState.isCurrentlyGrading ?? 0) > 0) return false;
  if (anySetupStepOngoing(examState)) return false;

  return !(await anyLiveJobForTypes(examId, stepTypesOf('grading')));
}

/**
 * The single gate distribution asks: may this exam's results be released?
 *
 * Both conditions are required and neither implies the other — an exam mid-grading has no confidences yet
 * and so reports no open clarifications, while a fully graded one can still be riddled with them.
 */
export async function resolveResultsBlockedReason(
  exam: Exam | Record<string, unknown>,
  examId: string,
  examState: ExamState | undefined,
  submissions: unknown[],
): Promise<ResultsBlockedReason | null> {
  if (submissions.length === 0) return 'no-submissions';
  if (!(await isGradingComplete(examId, examState))) return 'grading-incomplete';
  if (hasOpenClarifications(exam, submissions)) return 'open-clarifications';
  return null;
}
